import { ChevronsUpDownIcon, LanguagesIcon, LogOutIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar'
import { useAuth } from '@/auth/useAuth'
import { SUPPORTED_LANGUAGES, currentLanguage, setLanguage, type Language } from '@/i18n'
import { fullName, initials } from '@/lib/userDisplay'

/**
 * The account menu, in the sidebar footer where every shadcn sidebar block puts it: language and
 * sign-out, plus the identity the two apply to.
 */
export function NavUser() {
  const { t } = useTranslation()
  const { isMobile } = useSidebar()
  const { user, logout } = useAuth()

  const name = user ? fullName(user) : ''
  // Somebody invited but not yet completed has no name yet; the address is all there is to show.
  const primary = name || user?.email || ''
  const secondary = name ? user?.email : undefined

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              tooltip={primary}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-8">
                <AvatarFallback className="text-xs">{user ? initials(user) : '?'}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{primary}</span>
                {secondary && (
                  <span className="text-muted-foreground truncate text-xs">{secondary}</span>
                )}
              </div>
              <ChevronsUpDownIcon className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="font-normal">
              <span className="block truncate font-medium">{primary}</span>
              {secondary && (
                <span className="text-muted-foreground block truncate text-xs">{secondary}</span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <LanguagesIcon />
                {t('language.label')}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup
                    value={currentLanguage()}
                    onValueChange={(value) => setLanguage(value as Language)}
                  >
                    {SUPPORTED_LANGUAGES.map((language) => (
                      // Endonyms, not translations: "Deutsch" reads as Deutsch in every UI
                      // language, which is what someone stuck in the wrong one is looking for.
                      <DropdownMenuRadioItem key={language} value={language}>
                        {t(`language.${language}`)}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void logout()}>
              <LogOutIcon />
              {t('account.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
